import React from 'react';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

// Errors that almost always indicate a stale cached JS bundle (e.g. user kept
// the page open while we deployed a new build). These get a one-shot hard reload
// to pick up the fresh bundle, bypassing the in-memory closure.
const STALE_BUNDLE_PATTERNS = [
  /Can't find variable: draw/i,
  /drawStarrySky is not defined/i,
  /drawCityView is not defined/i,
  /drawStringLights is not defined/i,
  /drawRooftop\w+ is not defined/i,
  /drawLoungeSeating is not defined/i,
  /Loading chunk \d+ failed/i,           // CRA chunk-load fail after new deploy
  /ChunkLoadError/i,
  /Unexpected token '?<'?/i,             // index.html served as JS chunk
];
const RELOAD_FLAG = 'razient_stale_bundle_reload_at';

function looksLikeStaleBundle(error) {
  if (!error) return false;
  const msg = String(error?.message || error);
  return STALE_BUNDLE_PATTERNS.some((re) => re.test(msg));
}

function attemptOneShotReload() {
  try {
    const last = parseInt(sessionStorage.getItem(RELOAD_FLAG) || '0', 10);
    const now = Date.now();
    // Only reload once per 30s window — prevent infinite loops if the bundle is
    // genuinely broken (not just cached-stale).
    if (now - last > 30000) {
      sessionStorage.setItem(RELOAD_FLAG, String(now));
      // Force a network revalidation of all assets.
      window.location.reload();
      return true;
    }
  } catch {
    // sessionStorage unavailable — best-effort reload anyway, but only once per
    // page load.
    if (!window.__razientReloadedOnce) {
      window.__razientReloadedOnce = true;
      window.location.reload();
      return true;
    }
  }
  return false;
}

// Catch errors from event handlers / promises that React's componentDidCatch
// doesn't see (unhandled rejection, window.onerror).
if (typeof window !== 'undefined' && !window.__razientStaleBundleHookInstalled) {
  window.__razientStaleBundleHookInstalled = true;
  window.addEventListener('error', (e) => {
    if (looksLikeStaleBundle(e?.error || e?.message)) {
      attemptOneShotReload();
    }
  });
  window.addEventListener('unhandledrejection', (e) => {
    if (looksLikeStaleBundle(e?.reason)) {
      attemptOneShotReload();
    }
  });
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    // If this looks like a stale-bundle error, transparently reload once.
    if (looksLikeStaleBundle(error)) {
      attemptOneShotReload();
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="glass-default rounded-2xl p-8 max-w-md text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="text-red-400" size={32} />
            </div>
            <h2 className="text-2xl font-bold mb-2">Oops! Something went wrong</h2>
            <p className="text-white/60 mb-4">
              We encountered an unexpected error. Please try again or return to the home page.
            </p>
            {this.state.error && (
              <div className="bg-black/30 rounded p-3 mb-4 text-left text-xs text-red-400 overflow-auto max-h-32">
                <strong>Error:</strong> {this.state.error.toString()}
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <Button onClick={this.handleRetry} className="bg-violet-600 hover:bg-violet-500">
                <RefreshCw size={18} className="mr-2" />
                Try Again
              </Button>
              <Button onClick={this.handleGoHome} variant="outline" className="border-white/20">
                <Home size={18} className="mr-2" />
                Go Home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
